import { RaytracedDieMaterial, TABLE_PRESETS } from './tablePresets';

export interface DieRenderParams {
  /** Die value 0..6 (0 = unrolled state before first roll) */
  value: number;
  /** Whether the die is locked/kept in the keep tray */
  isLocked: boolean;
  /** Extra 3D Euler rotation offsets (in radians) applied in camera space */
  tiltX: number;
  tiltY: number;
  tiltZ: number;
  /** Vertical lift (0..1) during roll bounce or hover */
  lift: number;
  /** Material definition from current 3D table preset */
  material: RaytracedDieMaterial;
}

/**
 * 3x3 column-major rotation matrix helper.
 */
type Mat3 = Float32Array;

function mat3Multiply(a: Mat3, b: Mat3): Mat3 {
  const out = new Float32Array(9);
  for (let col = 0; col < 3; col++) {
    for (let row = 0; row < 3; row++) {
      out[col * 3 + row] =
        a[0 * 3 + row] * b[col * 3 + 0] +
        a[1 * 3 + row] * b[col * 3 + 1] +
        a[2 * 3 + row] * b[col * 3 + 2];
    }
  }
  return out;
}

function mat3RotX(rad: number): Mat3 {
  const c = Math.cos(rad);
  const s = Math.sin(rad);
  // Column-major:
  // [ 1  0  0 ]
  // [ 0  c -s ]
  // [ 0  s  c ]
  return new Float32Array([
    1, 0, 0,
    0, c, s,
    0, -s, c,
  ]);
}

function mat3RotY(rad: number): Mat3 {
  const c = Math.cos(rad);
  const s = Math.sin(rad);
  // [  c  0  s ]
  // [  0  1  0 ]
  // [ -s  0  c ]
  return new Float32Array([
    c, 0, -s,
    0, 1, 0,
    s, 0, c,
  ]);
}

function mat3RotZ(rad: number): Mat3 {
  const c = Math.cos(rad);
  const s = Math.sin(rad);
  // [ c -s  0 ]
  // [ s  c  0 ]
  // [ 0  0  1 ]
  return new Float32Array([
    c, s, 0,
    -s, c, 0,
    0, 0, 1,
  ]);
}

function mat3Transpose(m: Mat3): Mat3 {
  return new Float32Array([
    m[0], m[3], m[6],
    m[1], m[4], m[7],
    m[2], m[5], m[8],
  ]);
}

/**
 * Returns the base rotation matrix that brings the given die face (1..6)
 * to point toward +Z (directly facing the camera).
 *
 * Face layout in local die space:
 *  +Z = 1
 *  -Z = 6
 *  +Y = 2
 *  -Y = 5
 *  +X = 3
 *  -X = 4
 */
export function getFaceBaseMatrix(value: number): Mat3 {
  switch (value) {
    case 1:
      return mat3RotY(0);
    case 6:
      return mat3RotY(Math.PI);
    case 2:
      return mat3RotX(Math.PI / 2);
    case 5:
      return mat3RotX(-Math.PI / 2);
    case 3:
      return mat3RotY(-Math.PI / 2);
    case 4:
      return mat3RotY(Math.PI / 2);
    default:
      // Value 0 (unrolled): showcase isometric corner angle
      return mat3Multiply(mat3RotX(0.42), mat3RotY(-0.55));
  }
}

/**
 * Computes the inverse rotation matrix (world -> local die space) for the shader.
 */
export function buildInvRotationMatrix(value: number, tiltX: number, tiltY: number, tiltZ: number): Mat3 {
  const base = getFaceBaseMatrix(value);
  const tilt = mat3Multiply(mat3RotZ(tiltZ), mat3Multiply(mat3RotX(tiltX), mat3RotY(tiltY)));
  const worldRot = mat3Multiply(tilt, base);
  return mat3Transpose(worldRot);
}

/**
 * Computes deterministic resting 3D perspective tilt for a die so you see the
 * top face clearly AND two beveled 3D sides with their recessed pips.
 */
export function getRestingTilt(index: number, isLocked: boolean, isUnrolled: boolean): {
  tiltX: number;
  tiltY: number;
  tiltZ: number;
} {
  if (isLocked) {
    // Neat, uniform 3D presentation angle in the Keep Tray
    return {
      tiltX: 0.22,
      tiltY: -0.20,
      tiltZ: 0,
    };
  }
  if (isUnrolled) {
    const sign = index % 2 === 0 ? 1 : -1;
    return {
      tiltX: 0.34,
      tiltY: -0.38 * sign,
      tiltZ: ((index * 37) % 9 - 4) * 0.02,
    };
  }
  // Natural 3D perspective on the rolling table: top face ~82% facing camera,
  // with clear 3D side bevels visible on bottom/right or bottom/left.
  const dirX = index % 2 === 0 ? 1 : -1;
  const zDeg = ((index * 47) % 11) - 5; // -5° .. +5°
  return {
    tiltX: 0.26 + (index % 3) * 0.025,
    tiltY: dirX * (0.23 + ((index + 1) % 3) * 0.02),
    tiltZ: (zDeg * Math.PI) / 180,
  };
}

const VERTEX_SHADER_SRC = `
attribute vec2 aPos;
varying vec2 vUv;
void main() {
  vUv = aPos;
  gl_Position = vec4(aPos, 0.0, 1.0);
}
`;

/**
 * Real-time GLSL Signed Distance Field (SDF) Raytracer for a luxury beveled 3D die
 * with recessed spherical pips on all 6 faces, PBR reflections, subsurface scattering,
 * ambient occlusion, and raytraced soft contact shadows.
 */
const FRAGMENT_SHADER_SRC = `
precision highp float;
varying vec2 vUv;

uniform mat3 uInvRot;
uniform vec3 uBodyColor;
uniform vec3 uPipColor;
uniform vec3 uPipOneColor;
uniform vec3 uSssColor;
uniform vec3 uLockGlowColor;
uniform vec3 uTableBounceColor;
uniform float uShininess;
uniform float uReflectivity;
uniform float uIsLocked;
uniform float uLift;
uniform float uUnrolled;

// Smooth maximum for chamfered spherical cube intersection
float smax(float a, float b, float k) {
  float h = clamp(0.5 + 0.5 * (a - b) / k, 0.0, 1.0);
  return mix(b, a, h) + k * h * (1.0 - h);
}

// Rounded box SDF
float sdRoundBox(vec3 p, vec3 b, float r) {
  vec3 q = abs(p) - b;
  return length(max(q, 0.0)) + min(max(q.x, max(q.y, q.z)), 0.0) - r;
}

// Evaluates the 3D die body and all 6 faces' spherical pips.
// Returns vec2(signedDistance, materialId)
// materialId:
//   1.0 = polished die body
//   2.0 = recessed pip (faces 2..6)
//   3.0 = recessed center pip on #1 face
vec2 mapDie(vec3 pLocal) {
  // Luxury casino die body: rounded box intersected with a sphere for chamfered corners
  float box = sdRoundBox(pLocal, vec3(0.54), 0.14);
  float cornerSphere = length(pLocal) - 0.885;
  float body = smax(box, cornerSphere, 0.045);

  // Distance to nearest pip sphere across all 6 faces
  float pipDist = 10.0;
  float isOnePip = 0.0;

  // Face depth offset where pip centers sit just outside the 0.68 cube surface
  const float F = 0.695;
  const float R_PIP = 0.132;
  const float R_ONE = 0.168;
  const float S = 0.285;
  const float S6Y = 0.305;

  // Optimize by checking which face(s) pLocal is near
  if (pLocal.z > 0.35) {
    // +Z: Face 1 (single large center pip)
    float d1 = length(pLocal - vec3(0.0, 0.0, F)) - R_ONE;
    if (d1 < pipDist) {
      pipDist = d1;
      isOnePip = 1.0;
    }
  } else if (pLocal.z < -0.35) {
    // -Z: Face 6 (2 columns x 3 rows)
    vec3 q = vec3(abs(pLocal.x), pLocal.y, pLocal.z);
    float d6a = length(q - vec3(S, S6Y, -F)) - R_PIP;
    float d6b = length(q - vec3(S, 0.0, -F)) - R_PIP;
    float d6c = length(q - vec3(S, -S6Y, -F)) - R_PIP;
    pipDist = min(pipDist, min(d6a, min(d6b, d6c)));
  }

  if (pLocal.y > 0.35) {
    // +Y: Face 2 (diagonal)
    float d2a = length(pLocal - vec3(-S, F, -S)) - R_PIP;
    float d2b = length(pLocal - vec3(S, F, S)) - R_PIP;
    pipDist = min(pipDist, min(d2a, d2b));
  } else if (pLocal.y < -0.35) {
    // -Y: Face 5 (4 corners + center)
    vec3 q = vec3(abs(pLocal.x), pLocal.y, abs(pLocal.z));
    float d5Corner = length(q - vec3(S, -F, S)) - R_PIP;
    float d5Center = length(pLocal - vec3(0.0, -F, 0.0)) - R_PIP;
    pipDist = min(pipDist, min(d5Corner, d5Center));
  }

  if (pLocal.x > 0.35) {
    // +X: Face 3 (diagonal + center)
    float d3a = length(pLocal - vec3(F, -S, -S)) - R_PIP;
    float d3b = length(pLocal - vec3(F, 0.0, 0.0)) - R_PIP;
    float d3c = length(pLocal - vec3(F, S, S)) - R_PIP;
    pipDist = min(pipDist, min(d3a, min(d3b, d3c)));
  } else if (pLocal.x < -0.35) {
    // -X: Face 4 (4 corners)
    vec3 q = vec3(pLocal.x, abs(pLocal.y), abs(pLocal.z));
    float d4 = length(q - vec3(-F, S, S)) - R_PIP;
    pipDist = min(pipDist, d4);
  }

  // Smoothly carve pips out of the die body
  float carved = smax(body, -pipDist, 0.018);
  float matId = (pipDist < body + 0.022) ? (isOnePip > 0.5 ? 3.0 : 2.0) : 1.0;
  return vec2(carved, matId);
}

vec2 mapWorld(vec3 pWorld) {
  vec3 shifted = pWorld - vec3(0.0, uLift * 0.14, uLift * 0.18);
  vec3 pLocal = uInvRot * shifted;
  return mapDie(pLocal);
}

vec3 calcNormal(vec3 p) {
  const float e = 0.0018;
  vec2 h = vec2(e, -e);
  return normalize(
    h.xyy * mapWorld(p + h.xyy).x +
    h.yyx * mapWorld(p + h.yyx).x +
    h.yxy * mapWorld(p + h.yxy).x +
    h.xxx * mapWorld(p + h.xxx).x
  );
}

float calcAO(vec3 pos, vec3 nor) {
  float occ = 0.0;
  float sca = 1.0;
  for (int i = 0; i < 4; i++) {
    float h = 0.02 + 0.055 * float(i);
    float d = mapWorld(pos + h * nor).x;
    occ += (h - d) * sca;
    sca *= 0.65;
  }
  return clamp(1.0 - 2.6 * occ, 0.0, 1.0);
}

void main() {
  // Camera setup
  vec2 uv = vUv * 1.16;
  vec3 ro = vec3(0.0, 0.0, 2.95);
  vec3 rd = normalize(vec3(uv, -1.85));

  // Raymarch the 3D die
  float t = 1.2;
  float hitMat = 0.0;
  for (int i = 0; i < 56; i++) {
    vec3 p = ro + rd * t;
    vec2 res = mapWorld(p);
    if (res.x < 0.0014) {
      hitMat = res.y;
      break;
    }
    t += res.x;
    if (t > 4.3) break;
  }

  if (hitMat > 0.5) {
    vec3 pos = ro + rd * t;
    vec3 nor = calcNormal(pos);
    vec3 viewDir = -rd;
    float ao = calcAO(pos, nor);

    // Three-point studio raytraced lighting + table bounce light
    vec3 keyLightDir = normalize(vec3(-0.52, 0.68, 0.85));
    vec3 rimLightDir = normalize(vec3(0.75, 0.42, 0.35));
    vec3 bounceDir = normalize(vec3(0.0, -0.75, -0.65));

    float diffKey = max(dot(nor, keyLightDir), 0.0);
    float diffRim = max(dot(nor, rimLightDir), 0.0);
    float diffBounce = max(dot(nor, bounceDir), 0.0);

    // Base albedo selection
    vec3 baseCol = uBodyColor;
    float isPip = step(1.5, hitMat);
    if (hitMat > 2.5) {
      baseCol = uPipOneColor;
    } else if (hitMat > 1.5) {
      baseCol = uPipColor;
    }

    // Subsurface scattering (light penetrating bevels & translucent acrylic/ivory)
    float sss = pow(clamp(dot(viewDir, -keyLightDir + nor * 0.45), 0.0, 1.0), 2.4) * (1.0 - isPip * 0.8);
    vec3 sssContrib = uSssColor * sss * 0.32;

    // Fresnel (Schlick approximation)
    float fresnel = pow(1.0 - max(dot(nor, viewDir), 0.0), 3.6);

    // Dual-lobe specular reflection (sharp clearcoat + broad satin sheen)
    vec3 halfKey = normalize(keyLightDir + viewDir);
    vec3 halfRim = normalize(rimLightDir + viewDir);
    float specSharp = pow(max(dot(nor, halfKey), 0.0), uShininess);
    float specBroad = pow(max(dot(nor, halfKey), 0.0), 18.0);
    float specRim = pow(max(dot(nor, halfRim), 0.0), 42.0);

    // Procedural studio softbox environment reflection
    vec3 refl = reflect(-viewDir, nor);
    float studioBox1 = smoothstep(0.62, 0.92, dot(refl, normalize(vec3(-0.45, 0.65, 0.62))));
    float studioBox2 = smoothstep(0.70, 0.96, dot(refl, normalize(vec3(0.60, -0.35, 0.72))));
    vec3 envRefl = vec3(1.0, 0.99, 0.96) * studioBox1 * 0.48 + uLockGlowColor * studioBox2 * 0.22;

    // Combine diffuse + ambient + bounce + SSS
    vec3 ambient = baseCol * 0.34;
    vec3 diffuse = baseCol * (diffKey * 0.72 + diffRim * 0.22);
    vec3 bounce = mix(uTableBounceColor, uLockGlowColor, uIsLocked * 0.7) * diffBounce * 0.28 * (1.0 - isPip * 0.5);

    vec3 col = (ambient + diffuse + bounce + sssContrib) * ao;

    // Pips have recessed inner shadow + enamel rim highlight
    if (isPip > 0.5) {
      col *= 0.88;
      col += vec3(1.0) * specSharp * 0.45;
    } else {
      col += vec3(1.0, 0.99, 0.95) * (specSharp * 0.72 + specBroad * 0.16) * uReflectivity;
      col += mix(vec3(0.85, 0.92, 1.0), uLockGlowColor, uIsLocked) * specRim * 0.28;
      col += envRefl * fresnel * uReflectivity;
    }

    // Locked die gets an illuminated golden/emerald rim glow
    if (uIsLocked > 0.5) {
      col += uLockGlowColor * fresnel * 0.42;
    }

    // Unrolled state (before first roll): slightly dreamy studio sheen
    if (uUnrolled > 0.5) {
      col = mix(col, col * 0.92 + vec3(0.08, 0.10, 0.14), 0.22);
    }

    // Filmic tone curve & gamma
    col = col / (1.0 + col * 0.18);
    col = pow(clamp(col, 0.0, 1.0), vec3(0.92));

    // Smooth anti-aliased silhouette edge
    float edgeDist = mapWorld(pos).x;
    float alpha = 1.0;
    gl_FragColor = vec4(col, alpha);
    return;
  }

  // Ray missed the die: render soft raytraced contact shadow + lock halo on table plane
  vec2 shadowCenter = vec2(0.05, -0.08 - uLift * 0.06);
  float distToCenter = length(uv - shadowCenter);
  float shadowRadius = 0.82 + uLift * 0.16;
  float softShadow = exp(-pow(distToCenter / shadowRadius, 2.4) * 3.1) * (0.48 - uLift * 0.18);

  if (uIsLocked > 0.5) {
    float ring = exp(-pow((distToCenter - 0.74) / 0.16, 2.0)) * 0.52;
    vec3 glowCol = uLockGlowColor;
    float totalAlpha = clamp(max(softShadow * 0.7, ring), 0.0, 0.75);
    vec3 finalRgb = glowCol * (ring / max(totalAlpha, 0.001));
    gl_FragColor = vec4(finalRgb, totalAlpha);
  } else {
    gl_FragColor = vec4(0.0, 0.0, 0.0, clamp(softShadow, 0.0, 0.55));
  }
}
`;

interface WebGLRaytracerContext {
  canvas: HTMLCanvasElement;
  gl: WebGLRenderingContext;
  program: WebGLProgram;
  uniforms: Record<string, WebGLUniformLocation | null>;
}

let sharedRaytracer: WebGLRaytracerContext | null = null;
let webglUnavailable = false;
const frameCache = new Map<string, HTMLCanvasElement>();
const MAX_CACHE_ENTRIES = 160;

function initSharedRaytracer(): WebGLRaytracerContext | null {
  if (sharedRaytracer) return sharedRaytracer;
  if (webglUnavailable || typeof document === 'undefined') return null;

  try {
    // Skip WebGL probing in jsdom where getContext returns null or throws not-implemented
    if (typeof navigator !== 'undefined' && /jsdom/i.test(navigator.userAgent || '')) {
      webglUnavailable = true;
      return null;
    }

    const canvas = document.createElement('canvas');
    canvas.width = 220;
    canvas.height = 220;

    const gl = (
      canvas.getContext('webgl2', { alpha: true, antialias: true, premultipliedAlpha: false }) ||
      canvas.getContext('webgl', { alpha: true, antialias: true, premultipliedAlpha: false })
    ) as WebGLRenderingContext | null;

    if (!gl) {
      webglUnavailable = true;
      return null;
    }

    const compileShader = (type: number, src: string) => {
      const shader = gl.createShader(type);
      if (!shader) return null;
      gl.shaderSource(shader, src);
      gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        gl.deleteShader(shader);
        return null;
      }
      return shader;
    };

    const vs = compileShader(gl.VERTEX_SHADER, VERTEX_SHADER_SRC);
    const fs = compileShader(gl.FRAGMENT_SHADER, FRAGMENT_SHADER_SRC);
    if (!vs || !fs) {
      webglUnavailable = true;
      return null;
    }

    const program = gl.createProgram();
    if (!program) {
      webglUnavailable = true;
      return null;
    }
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      webglUnavailable = true;
      return null;
    }

    gl.useProgram(program);

    // Fullscreen quad
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
      gl.STATIC_DRAW
    );

    const aPos = gl.getAttribLocation(program, 'aPos');
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

    const uniformNames = [
      'uInvRot',
      'uBodyColor',
      'uPipColor',
      'uPipOneColor',
      'uSssColor',
      'uLockGlowColor',
      'uTableBounceColor',
      'uShininess',
      'uReflectivity',
      'uIsLocked',
      'uLift',
      'uUnrolled',
    ];
    const uniforms: Record<string, WebGLUniformLocation | null> = {};
    for (const name of uniformNames) {
      uniforms[name] = gl.getUniformLocation(program, name);
    }

    sharedRaytracer = { canvas, gl, program, uniforms };
    return sharedRaytracer;
  } catch {
    webglUnavailable = true;
    return null;
  }
}

function rgbCss(rgb: [number, number, number], alpha = 1): string {
  const r = Math.round(Math.min(1, Math.max(0, rgb[0])) * 255);
  const g = Math.round(Math.min(1, Math.max(0, rgb[1])) * 255);
  const b = Math.round(Math.min(1, Math.max(0, rgb[2])) * 255);
  return alpha < 1 ? `rgba(${r},${g},${b},${alpha})` : `rgb(${r},${g},${b})`;
}

const PIP_LAYOUT_2D: Record<number, [number, number][]> = {
  0: [],
  1: [[0, 0]],
  2: [[-0.27, -0.27], [0.27, 0.27]],
  3: [[-0.28, -0.28], [0, 0], [0.28, 0.28]],
  4: [[-0.27, -0.27], [0.27, -0.27], [-0.27, 0.27], [0.27, 0.27]],
  5: [[-0.28, -0.28], [0.28, -0.28], [0, 0], [-0.28, 0.28], [0.28, 0.28]],
  6: [
    [-0.27, -0.30], [0.27, -0.30],
    [-0.27, 0.0], [0.27, 0.0],
    [-0.27, 0.30], [0.27, 0.30],
  ],
};

/**
 * High-precision 2D Canvas 3D-shaded fallback when WebGL is unavailable.
 */
function renderCanvas2DFallback(
  ctx: CanvasRenderingContext2D,
  pixelSize: number,
  params: DieRenderParams
): void {
  const { value, isLocked, tiltX, tiltY, tiltZ, lift, material } = params;
  ctx.clearRect(0, 0, pixelSize, pixelSize);

  const cx = pixelSize / 2;
  const cy = pixelSize / 2 - lift * pixelSize * 0.04;
  const size = pixelSize * 0.68;
  const half = size / 2;
  const radius = size * 0.22;

  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(tiltZ * 0.5);

  // Soft shadow
  ctx.save();
  ctx.fillStyle = isLocked ? rgbCss(material.lockGlowColor, 0.38) : 'rgba(0, 0, 0, 0.36)';
  ctx.beginPath();
  ctx.ellipse(size * 0.04, size * 0.08 + lift * 6, half * 0.98, half * 0.92, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  // 3D side extrusion based on tiltX / tiltY
  const extrudeX = Math.max(-size * 0.14, Math.min(size * 0.14, -tiltY * size * 0.32));
  const extrudeY = Math.max(-size * 0.14, Math.min(size * 0.14, tiltX * size * 0.32));

  const bodyRgb = isLocked ? material.lockedBodyColor : material.bodyColor;
  const darkerBody: [number, number, number] = [
    bodyRgb[0] * 0.76,
    bodyRgb[1] * 0.76,
    bodyRgb[2] * 0.78,
  ];

  // Draw extruded 3D bevel base
  ctx.fillStyle = rgbCss(darkerBody);
  ctx.beginPath();
  ctx.roundRect(-half + extrudeX, -half + extrudeY, size, size, radius);
  ctx.fill();

  // Draw main top face with studio gradient
  const grad = ctx.createLinearGradient(-half, -half, half, half);
  grad.addColorStop(0, rgbCss([
    Math.min(1, bodyRgb[0] * 1.06),
    Math.min(1, bodyRgb[1] * 1.06),
    Math.min(1, bodyRgb[2] * 1.06),
  ]));
  grad.addColorStop(1, rgbCss([
    bodyRgb[0] * 0.88,
    bodyRgb[1] * 0.88,
    bodyRgb[2] * 0.90,
  ]));

  ctx.fillStyle = grad;
  ctx.strokeStyle = isLocked ? rgbCss(material.lockGlowColor, 0.9) : 'rgba(255,255,255,0.45)';
  ctx.lineWidth = isLocked ? 3 : 1.5;
  ctx.beginPath();
  ctx.roundRect(-half, -half, size, size, radius);
  ctx.fill();
  ctx.stroke();

  // Draw recessed pips
  const pips = PIP_LAYOUT_2D[value] || [];
  const pipColor = isLocked
    ? material.lockedPipColor
    : value === 1
      ? material.pipOneColor
      : material.pipColor;

  for (const [px, py] of pips) {
    const x = px * size;
    const y = py * size;
    const r = (value === 1 ? 0.125 : 0.095) * size;

    // Inner recessed rim
    ctx.fillStyle = 'rgba(255, 255, 255, 0.45)';
    ctx.beginPath();
    ctx.arc(x + 0.8, y + 1.0, r, 0, Math.PI * 2);
    ctx.fill();

    // Pip cavity
    ctx.fillStyle = rgbCss(pipColor);
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();

    // Specular dot inside pip
    ctx.fillStyle = 'rgba(255, 255, 255, 0.32)';
    ctx.beginPath();
    ctx.arc(x - r * 0.28, y - r * 0.28, r * 0.32, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.restore();
}

function makeCacheKey(pixelSize: number, params: DieRenderParams): string {
  const { value, isLocked, tiltX, tiltY, tiltZ, lift, material } = params;
  const q = (n: number) => Math.round(n * 60);
  return [
    pixelSize,
    value,
    isLocked ? 1 : 0,
    q(tiltX),
    q(tiltY),
    q(tiltZ),
    q(lift),
    material.bodyColor.join(','),
    material.pipColor.join(','),
    material.pipOneColor.join(','),
  ].join('|');
}

/**
 * Renders a raytraced 3D die onto the target 2D canvas.
 * Uses a single shared WebGL raytracer context and caches resting frames.
 */
export function renderRaytracedDieToCanvas(
  targetCanvas: HTMLCanvasElement,
  params: DieRenderParams,
  useCache = true
): void {
  if (typeof navigator !== 'undefined' && /jsdom/i.test(navigator.userAgent || '')) {
    return;
  }

  let targetCtx: CanvasRenderingContext2D | null = null;
  try {
    targetCtx = targetCanvas.getContext('2d');
  } catch {
    return;
  }
  if (!targetCtx) return;

  const pixelSize = targetCanvas.width;
  if (pixelSize <= 0) return;

  const cacheKey = useCache ? makeCacheKey(pixelSize, params) : '';
  if (useCache && frameCache.has(cacheKey)) {
    const cached = frameCache.get(cacheKey)!;
    targetCtx.clearRect(0, 0, pixelSize, pixelSize);
    targetCtx.drawImage(cached, 0, 0, pixelSize, pixelSize);
    return;
  }

  const rt = initSharedRaytracer();
  if (!rt) {
    renderCanvas2DFallback(targetCtx, pixelSize, params);
    return;
  }

  const { gl, canvas: glCanvas, uniforms } = rt;
  if (glCanvas.width !== pixelSize || glCanvas.height !== pixelSize) {
    glCanvas.width = pixelSize;
    glCanvas.height = pixelSize;
  }

  gl.viewport(0, 0, pixelSize, pixelSize);
  gl.clearColor(0, 0, 0, 0);
  gl.clear(gl.COLOR_BUFFER_BIT);

  const invRot = buildInvRotationMatrix(params.value, params.tiltX, params.tiltY, params.tiltZ);
  const mat = params.material || TABLE_PRESETS.casino.dieMaterial;
  const body = params.isLocked ? mat.lockedBodyColor : mat.bodyColor;
  const pip = params.isLocked ? mat.lockedPipColor : mat.pipColor;
  const pipOne = params.isLocked ? mat.lockedPipColor : mat.pipOneColor;

  gl.uniformMatrix3fv(uniforms.uInvRot, false, invRot);
  gl.uniform3fv(uniforms.uBodyColor, body);
  gl.uniform3fv(uniforms.uPipColor, pip);
  gl.uniform3fv(uniforms.uPipOneColor, pipOne);
  gl.uniform3fv(uniforms.uSssColor, mat.sssColor);
  gl.uniform3fv(uniforms.uLockGlowColor, mat.lockGlowColor);
  gl.uniform3fv(uniforms.uTableBounceColor, mat.tableBounceColor);
  gl.uniform1f(uniforms.uShininess, mat.shininess);
  gl.uniform1f(uniforms.uReflectivity, mat.reflectivity);
  gl.uniform1f(uniforms.uIsLocked, params.isLocked ? 1.0 : 0.0);
  gl.uniform1f(uniforms.uLift, params.lift);
  gl.uniform1f(uniforms.uUnrolled, params.value === 0 ? 1.0 : 0.0);

  gl.drawArrays(gl.TRIANGLES, 0, 6);

  targetCtx.clearRect(0, 0, pixelSize, pixelSize);
  targetCtx.drawImage(glCanvas, 0, 0, pixelSize, pixelSize);

  if (useCache) {
    try {
      if (frameCache.size >= MAX_CACHE_ENTRIES) {
        const firstKey = frameCache.keys().next().value;
        if (firstKey) frameCache.delete(firstKey);
      }
      const snap = document.createElement('canvas');
      snap.width = pixelSize;
      snap.height = pixelSize;
      const snapCtx = snap.getContext('2d');
      if (snapCtx) {
        snapCtx.drawImage(glCanvas, 0, 0);
        frameCache.set(cacheKey, snap);
      }
    } catch {}
  }
}
